import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type {
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';

import GqlClientContext from 'contexts/GqlClientContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import BonusPeriodsContext from 'contexts/BonusPeriodsContext';
import useDateScope from 'hooks/useDateScope';
import captureException from 'services/captureException';
import runAggregation from 'components/GlobalSearch/runAggregation';

import useEntityField from '../../TargetManager/hooks/useEntityField';
import fetchPeriodOverrideMarkers, {
  OverrideMarkers,
} from './fetchPeriodOverrideMarkers';

const NUMERIC_FIELD_TYPES: FleetOps.FieldType[] = ['float', 'long', 'int'];

/**
 * Pick the agg func for a sign-off board field: date → first (the period's first
 * value, e.g. the week start), numeric-ish → sum, everything else → last. Date
 * fields use `first` regardless of their declared UI aggFuncs (which are
 * count/min/max) — aggregateCompoundMetric supports it, and without this they
 * fall through to `last`, which they don't allow, and get dropped from the query.
 * Falls back to `last`, and returns undefined when the field can't be aggregated
 * at all (so it's dropped from the query).
 */
export const getSignOffAggFunc = (
  field: FleetOps.Field,
): AggFunc | undefined => {
  if (field.type === 'date') {
    return 'first';
  }
  const desired: AggFunc = NUMERIC_FIELD_TYPES.includes(field.type)
    ? 'sum'
    : 'last';
  if (field.aggFuncs.includes(desired)) {
    return desired;
  }
  if (field.aggFuncs.includes('last')) {
    return 'last';
  }
  return undefined;
};

// An aggregated bucket, optionally carrying the driver's override markers.
type AggregatedRow = {
  [key: string]:
    | string
    | number
    | undefined
    | OverrideMarkers[keyof OverrideMarkers];
};

const compareBuckets = (
  a: AggregatedRow,
  b: AggregatedRow,
  colId: string,
  sort: 'asc' | 'desc',
) => {
  const av = a[colId];
  const bv = b[colId];
  const direction = sort === 'desc' ? -1 : 1;
  if (av === bv) {
    return 0;
  }
  if (av === undefined) {
    return 1;
  }
  if (bv === undefined) {
    return -1;
  }
  if (typeof av === 'number' && typeof bv === 'number') {
    return (av - bv) * direction;
  }
  return String(av).localeCompare(String(bv)) * direction;
};

// The first sort key's order, as the aggregated datasource has always sorted
// (client-side, on the first column of the sort model only)
export const sortAggregatedRows = ({
  rows,
  sortModel,
}: {
  rows: AggregatedRow[];
  sortModel: { colId: string; sort: 'asc' | 'desc' }[];
}) => {
  const [firstSort] = sortModel;
  return firstSort
    ? [...rows].sort((a, b) =>
        compareBuckets(a, b, firstSort.colId, firstSort.sort),
      )
    : rows;
};

/**
 * Loads a sign-off board's rows for a sub-period programme: instead of
 * paginating raw weekly `search2` documents, aggregate the board's columns by
 * driver over the parent period via aggregateCompoundMetric (date → first,
 * numeric → sum, text → last). Each returned bucket is already a row keyed by
 * field id + the driver field, so the existing colDefs render unchanged.
 */
const useLoadAggregatedRows = ({
  board,
  filters,
}: {
  board: PerformanceBoardTypes.Board;
  filters: FilterInput[];
}) => {
  const { client } = useContext(GqlClientContext);
  const { datasets, performanceDatasets } = useContext(
    DatasetDefinitionsContext,
  );
  const dateScope = useDateScope({});
  const entityField = useEntityField(board.dataType);
  const { selectedBonusPeriod } = useContext(BonusPeriodsContext);
  const subPeriods = useMemo(
    () =>
      selectedBonusPeriod && 'subPeriods' in selectedBonusPeriod
        ? selectedBonusPeriod.subPeriods ?? []
        : [],
    [selectedBonusPeriod],
  );

  const metrics = useMemo((): MetricInput[] => {
    const datasetDefinition = [...datasets, ...performanceDatasets].find(
      (d) => d.type === board.dataType,
    );
    if (!datasetDefinition) {
      return [];
    }
    const fieldLookup = new Map(
      datasetDefinition.fields.map((f) => [f.field, f]),
    );

    return board.columnOrder
      .filter(
        (column) =>
          column.visible &&
          column.type === 'field' &&
          column.field !== entityField,
      )
      .map((column): MetricInput | undefined => {
        const field = fieldLookup.get(column.field);
        if (!field) {
          return undefined;
        }
        const aggFunc = getSignOffAggFunc(field);
        if (!aggFunc) {
          return undefined;
        }
        return {
          id: column.field,
          field: column.field,
          dataType: board.dataType,
          aggFunc,
          filters: {},
        };
      })
      .filter((metric): metric is MetricInput => metric !== undefined);
  }, [
    board.columnOrder,
    board.dataType,
    datasets,
    performanceDatasets,
    entityField,
  ]);

  return useCallback(async (): Promise<AggregatedRow[]> => {
    if (!dateScope || !entityField) {
      return [];
    }

    // Aggregated buckets don't carry the backend override enrichment the
    // driver-column indicator reads, so backfill it from the weekly docs
    // (tagged per week). Best-effort: a failure just drops the indicators.
    const markersPromise = fetchPeriodOverrideMarkers({
      client,
      dataType: board.dataType,
      filters,
      dateScope,
      entityField,
      subPeriods,
    }).catch((ex): Record<string, OverrideMarkers> => {
      captureException(ex);
      return {};
    });

    const [result, markersByDriver] = await Promise.all([
      runAggregation({
        client,
        metrics,
        dateScope,
        filters,
        groupBy: { field: entityField, type: 'text' },
      }),
      markersPromise,
    ]);
    return result.map((bucket) => {
      const driver = bucket[entityField];
      const markers =
        typeof driver === 'string' ? markersByDriver[driver] : undefined;
      return markers ? { ...bucket, ...markers } : bucket;
    });
  }, [
    client,
    metrics,
    dateScope,
    filters,
    entityField,
    board.dataType,
    subPeriods,
  ]);
};

/**
 * The aggregated rows (see useLoadAggregatedRows) as an AG server-side
 * datasource: loaded once, then sorted client-side from the ag-grid sort
 * model. Returned as `{ dataSource }` to mirror `useDatasource` so the grid
 * can swap between them.
 */
const useAggregatedBonusDatasource = ({
  board,
  filters,
}: {
  board: PerformanceBoardTypes.Board;
  filters: FilterInput[];
}) => {
  const loadRows = useLoadAggregatedRows({ board, filters });

  const createDataSource = useCallback((): IServerSideDatasource => {
    let buckets: AggregatedRow[] | undefined;

    return {
      getRows(params: IServerSideGetRowsParams) {
        const respond = (rows: AggregatedRow[]) => {
          const sorted = sortAggregatedRows({
            rows,
            sortModel: params.request.sortModel,
          });
          const start = params.request.startRow ?? 0;
          const end = params.request.endRow ?? sorted.length;
          params.success({
            rowData: sorted.slice(start, end),
            rowCount: sorted.length,
          });
        };

        if (buckets) {
          respond(buckets);
          return;
        }

        loadRows()
          .then((rows) => {
            buckets = rows;
            respond(rows);
          })
          .catch((ex) => {
            captureException(ex);
            params.fail();
          });
      },
    };
  }, [loadRows]);

  const [dataSource, setDataSource] = useState<IServerSideDatasource>();

  useEffect(() => {
    setDataSource(createDataSource());
  }, [createDataSource]);

  return { dataSource };
};

/**
 * The aggregated rows (see useLoadAggregatedRows) for DataTable: loaded once
 * per board, filters and period, and sorted as the AG datasource sorts them.
 */
export const useAggregatedBonusRows = ({
  board,
  filters,
  sortModel,
}: {
  board: PerformanceBoardTypes.Board;
  filters: FilterInput[];
  sortModel: { colId: string; sort: 'asc' | 'desc' }[];
}) => {
  const loadRows = useLoadAggregatedRows({ board, filters });
  const [rows, setRows] = useState<AggregatedRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    loadRows()
      .then((loaded) => {
        if (isActive) {
          setRows(loaded);
          setIsLoading(false);
        }
      })
      .catch((ex) => {
        captureException(ex);
        if (isActive) {
          setRows([]);
          setIsLoading(false);
        }
      });
    return () => {
      isActive = false;
    };
  }, [loadRows]);

  const sortedRows = useMemo(
    () => sortAggregatedRows({ rows, sortModel }),
    [rows, sortModel],
  );

  return { rows: sortedRows, isLoading };
};

export default useAggregatedBonusDatasource;
